import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "../../components/Pagination/Pagination";
import { ConfirmDialog } from "primereact/confirmdialog";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import {
  confirmDeleteInstitution,
  createOrUpdateInstitution,
  getInstitutionById,
  getInstitutionCategoryOptions,
  getInstitutions,
  getWardOptions,
  searchInstitutionsByName,
} from "./InstitutionService";

const emptyForm = {
  name: "",
  code: "",
  description: "",
  wardId: null,
  categoryId: null,
};

/**
 * Setup page for ast_asset_institution_names.
 * Relations: ward_id → wards, ast_asset_institution_category_id → categories.
 * Assets link via institution_id (+ denormalized institution_name).
 */
const Institutions = () => {
  document.title = "Institutions | Valitan IMS";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [wardOptions, setWardOptions] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({ name: "", category: "" });
  const [saving, setSaving] = useState(false);

  const [paginationInfo, setPaginationInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0,
    pageSize: 15,
    sort: {},
  });

  const loadInstitutions = useCallback(
    async (page = 0, size = paginationInfo.pageSize) => {
      setLoading(true);
      setSearching(false);
      try {
        const { items: rows, paginationInfo: info } = await getInstitutions(page, size);
        setItems(rows || []);
        setPaginationInfo(
          info || {
            totalElements: 0,
            totalPages: 0,
            pageNumber: 0,
            pageSize: size,
            sort: {},
          }
        );
      } finally {
        setLoading(false);
      }
    },
    [paginationInfo.pageSize]
  );

  useEffect(() => {
    loadInstitutions(0, paginationInfo.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationInfo.pageSize]);

  useEffect(() => {
    (async () => {
      const [cats, wards] = await Promise.all([
        getInstitutionCategoryOptions(),
        getWardOptions(),
      ]);
      setCategoryOptions(cats);
      setWardOptions(wards);
    })();
  }, []);

  const handleSearch = async () => {
    const q = searchTerm.trim();
    if (!q) {
      loadInstitutions(0, paginationInfo.pageSize);
      return;
    }
    setLoading(true);
    setSearching(true);
    try {
      const rows = await searchInstitutionsByName(q);
      setItems(rows);
      setPaginationInfo((prev) => ({
        ...prev,
        totalElements: rows.length,
        totalPages: 1,
        pageNumber: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModalOpen((open) => !open);
    if (modalOpen) {
      setEditingId(null);
      setForm(emptyForm);
      setErrors({ name: "", category: "" });
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setErrors({ name: "", category: "" });
    setModalOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const row = await getInstitutionById(id);
      if (!row) return;
      setEditingId(id);
      setForm({
        name: row.name || "",
        code: row.code || "",
        description: row.description || "",
        wardId: row.ward_id || row.wardId || null,
        categoryId:
          row.ast_asset_institution_category_id ||
          row.astAssetInstitutionCategoryId ||
          null,
      });
      setErrors({ name: "", category: "" });
      setModalOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await createOrUpdateInstitution({
        form,
        editingId,
        loadInstitutions: () =>
          searching
            ? handleSearch()
            : loadInstitutions(paginationInfo.pageNumber, paginationInfo.pageSize),
        toggleModal,
        setErrors,
      });
    } finally {
      setSaving(false);
    }
  };

  const startIndex = paginationInfo.pageNumber * paginationInfo.pageSize + 1;
  const selectedWard = wardOptions.find((o) => o.value === form.wardId) || null;
  const selectedCategory =
    categoryOptions.find((o) => o.value === form.categoryId) || null;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Settings" breadcrumbItem="Institutions" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <h4 className="card-title mb-0">Institutions</h4>
                  <Button color="primary" onClick={openCreate}>
                    <i className="ri-add-line align-bottom me-1" />
                    Add institution
                  </Button>
                </CardHeader>
                <CardBody>
                  <Row className="g-2 mb-3 align-items-end">
                    <Col md={6} lg={4}>
                      <Label className="small text-muted">Search by name</Label>
                      <Input
                        type="text"
                        value={searchTerm}
                        placeholder="e.g. Kalemaji Secondary…"
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearch();
                          }
                        }}
                      />
                    </Col>
                    <Col md="auto">
                      <Button color="info" outline onClick={handleSearch} disabled={loading}>
                        <i className="ri-search-line me-1" />
                        Search
                      </Button>
                    </Col>
                    <Col md="auto">
                      <Button
                        color="light"
                        className="border"
                        onClick={() => {
                          setSearchTerm("");
                          loadInstitutions(0, paginationInfo.pageSize);
                        }}
                        disabled={loading}
                      >
                        Clear
                      </Button>
                    </Col>
                  </Row>

                  <div className="table-responsive table-card">
                    <table className="table align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 50 }}>#</th>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Category</th>
                          <th>Ward</th>
                          <th style={{ width: 110 }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={6} className="text-center py-4">
                              <Spinner size="sm" className="me-2" />
                              Loading…
                            </td>
                          </tr>
                        ) : items.length > 0 ? (
                          items.map((row, index) => (
                            <tr key={row.id}>
                              <td>{searching ? index + 1 : startIndex + index}</td>
                              <td className="fw-semibold">{row.name}</td>
                              <td>
                                <code className="small">{row.code || "—"}</code>
                              </td>
                              <td>
                                {row.institution_category_name ||
                                  row.institutionCategoryName ||
                                  "—"}
                                {(row.institution_category_code ||
                                  row.institutionCategoryCode) && (
                                  <div className="small text-muted">
                                    {row.institution_category_code ||
                                      row.institutionCategoryCode}
                                  </div>
                                )}
                              </td>
                              <td>{row.ward_name || row.wardName || "—"}</td>
                              <td>
                                <div className="btn-group" role="group">
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    title="Edit"
                                    onClick={() => openEdit(row.id)}
                                  >
                                    <i className="ri-edit-box-line" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    title="Delete"
                                    onClick={() =>
                                      confirmDeleteInstitution(row.id, () =>
                                        searching
                                          ? handleSearch()
                                          : loadInstitutions(
                                              paginationInfo.pageNumber,
                                              paginationInfo.pageSize
                                            )
                                      )
                                    }
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-4">
                              No institutions found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {!searching && (
                    <Pagination
                      paginationInfo={paginationInfo}
                      handleNextPage={() => {
                        if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
                          loadInstitutions(
                            paginationInfo.pageNumber + 1,
                            paginationInfo.pageSize
                          );
                        }
                      }}
                      handlePreviousPage={() => {
                        if (paginationInfo.pageNumber > 0) {
                          loadInstitutions(
                            paginationInfo.pageNumber - 1,
                            paginationInfo.pageSize
                          );
                        }
                      }}
                      onPageChange={(pageNumber, pageSize) =>
                        loadInstitutions(pageNumber, pageSize)
                      }
                      onPageSizeChange={(newPageSize) => {
                        setPaginationInfo((prev) => ({
                          ...prev,
                          pageSize: newPageSize,
                          pageNumber: 0,
                        }));
                      }}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ConfirmDialog />

      <Modal isOpen={modalOpen} toggle={toggleModal} backdrop="static" size="lg">
        <ModalHeader toggle={toggleModal}>
          {editingId ? "Edit institution" : "Add institution"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={8}>
                <FormGroup>
                  <Label for="instName">Name *</Label>
                  <Input
                    id="instName"
                    value={form.name}
                    invalid={Boolean(errors.name)}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, name: e.target.value }));
                      setErrors((err) => ({ ...err, name: "" }));
                    }}
                    placeholder="e.g. Kalemaji Secondary School"
                  />
                  {errors.name && (
                    <div className="invalid-feedback d-block">{errors.name}</div>
                  )}
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="instCode">Code</Label>
                  <Input
                    id="instCode"
                    value={form.code}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                    placeholder="Auto from name if empty"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Institution category</Label>
                  <Select
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={(opt) =>
                      setForm((f) => ({ ...f, categoryId: opt?.value || null }))
                    }
                    isClearable
                    placeholder="Select category…"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Ward</Label>
                  <Select
                    options={wardOptions}
                    value={selectedWard}
                    onChange={(opt) =>
                      setForm((f) => ({ ...f, wardId: opt?.value || null }))
                    }
                    isClearable
                    placeholder="Select ward…"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label for="instDesc">Description</Label>
                  <Input
                    id="instDesc"
                    type="textarea"
                    rows={2}
                    value={form.description}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, description: e.target.value }))
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button type="button" color="light" onClick={toggleModal} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={saving}>
                {saving ? <Spinner size="sm" className="me-1" /> : null}
                {editingId ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default Institutions;
